"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";

import { useRouter } from "next/navigation";
import * as React from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { authService } from "@/api/auth";
import { ApiError, handleApiError } from "@/api/utils";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuthStore } from "@/lib/auth";

/**
 * Where a signed-in user lands. Inbox is home for every gym regardless of
 * which feature flags are on; Pulse and Stages are reached from the nav.
 */
const HOME = "/messages";

const formSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, { message: "Password must be at least 8 characters long" }),
});

export function LoginForm() {
  const router = useRouter();
  const auth = useAuthStore();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Signed-in visitors skip the form.
  React.useEffect(() => {
    if (auth.user) {
      router.replace(HOME);
    }
  }, [auth.user, router]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      await authService.login(values.email, values.password);
      router.replace(HOME);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err);
      }
    }
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="space-y-3">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Email" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input placeholder="Password" type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button type="submit" className="flex w-full" loading={form.formState.isSubmitting}>
            Submit
          </Button>
        </form>
      </Form>
      <div className="mt-4 text-center">
        <Button asChild variant="link" className="text-purple-600 shadow-none">
          <Link href="/auth/forgot-password">Forgot your password?</Link>
        </Button>
      </div>
    </>
  );
}
